<template>
  <div id="invest-page">
    <!-- Header -->
    <div class="page-head">
      <div class="page-head-text">
        <h1 class="page-title">My Investments</h1>
        <p class="page-subtitle">Track your investments and discover new opportunities</p>
      </div>
      <button class="refresh-btn" type="button" :disabled="refreshing" @click="refreshAll">
        <span v-if="refreshing" class="spinner-sm"></span>
        <InvIcon v-else name="arrow" class="refresh-icon" />
        Refresh
      </button>
    </div>

    <div class="invest-layout">
      <!-- ============ Summary (full-width banner) ============ -->
      <SummaryCard
        class="summary-block"
        :currency="activeCurrency"
        :total-shares="totalShares"
        :cash-value="totalCashValue"
        @change-currency="selectCurrency"
      />

      <!-- ============ Performance (wide growth + allocation) ============ -->
      <section class="section performance">
        <SectionHeader
          icon="chart-line"
          title="Portfolio Performance"
          :subtitle="`How your ${activeCurrency} holdings have grown`"
          color="#4E84FF"
        />
        <div class="perf-cards">
          <GrowthChart
            class="growth"
            :series="series"
            :currency="activeCurrency"
            :loading="performanceLoading"
            :period="activePeriod"
            :ago-label="periodAgoLabel"
            :current-value="currentValue"
            @change-period="selectPeriod"
          />
          <AllocationDonut class="allocation" :allocation="allocation" />
        </div>
      </section>

      <!-- ============ Portfolio + Activity ============ -->
      <div class="lower-grid">
        <!-- Portfolio holdings preview -->
        <section class="section">
          <SectionHeader
            icon="pie"
            title="Portfolio"
            :subtitle="`Your ${activeCurrency} holdings`"
            color="#F2994A"
          >
            <template #trailing>
              <span class="active-badge">
                <span class="active-count">{{ holdings.length }}</span>
                <span class="active-word">Active</span>
              </span>
            </template>
          </SectionHeader>

          <div class="card list-card">
            <div v-if="holdingsLoading" class="state-block">
              <span class="spinner"></span>
            </div>

            <template v-else-if="previewHoldings.length">
              <HoldingRow
                v-for="(t, i) in previewHoldings"
                :key="t._id || i"
                :token="t"
                :currency="activeCurrency"
                @select="goToHolding"
              />
              <button
                v-if="holdings.length > previewHoldings.length"
                class="see-all"
                type="button"
                @click="seeAllHoldings"
              >
                See all
                <InvIcon name="arrow" class="see-all-icon" />
              </button>
            </template>

            <div v-else class="empty-block">
              <span class="empty-icon blue">
                <InvIcon name="wallet" />
              </span>
              <p class="empty-title">No {{ activeCurrency }} holdings yet</p>
              <p class="empty-sub">Your {{ activeCurrency }} holdings will appear here</p>
            </div>
          </div>
        </section>

        <!-- Activity tabs -->
        <section class="section">
          <div class="tabbar">
            <button
              class="tab"
              :class="{ active: activeTab === 'maturities' }"
              type="button"
              @click="activeTab = 'maturities'"
            >
              Upcoming Maturities
            </button>
            <!-- Kept apart from Pending Orders on purpose: an order is a live offer to a
                 counterparty, a commitment is a claim awaiting allotment. Listing them
                 together would misrepresent both. -->
            <button
              class="tab"
              :class="{ active: activeTab === 'commitments' }"
              type="button"
              @click="activeTab = 'commitments'"
            >
              My Bids
            </button>
            <button
              class="tab"
              :class="{ active: activeTab === 'orders' }"
              type="button"
              @click="activeTab = 'orders'"
            >
              Pending Orders
            </button>
          </div>

          <!-- Upcoming Maturities -->
          <div v-if="activeTab === 'maturities'" class="tab-panel">
            <div v-if="maturities.length" class="stack">
              <MaturityCard
                v-for="(t, i) in maturities"
                :key="t._id || i"
                :token="t"
                :currency="activeCurrency"
              />
            </div>
            <div v-else class="empty-block card">
              <span class="empty-icon grey"><InvIcon name="event" /></span>
              <p class="empty-title">No upcoming maturities</p>
            </div>
          </div>

          <!-- Commitments -->
          <div v-else-if="activeTab === 'commitments'" class="tab-panel">
            <div v-if="commitmentList.length" class="stack">
              <CommitmentCard
                v-for="c in commitmentList"
                :key="c._id"
                :commitment="c"
                @withdraw="withdrawBid"
                @amend="amendBid"
              />
            </div>
            <div v-else class="empty-block card">
              <span class="empty-icon grey"><InvIcon name="event" /></span>
              <p class="empty-title">No bids yet</p>
              <p class="empty-sub">
                Bids you place into a book appear here until they are allotted.
              </p>
            </div>
          </div>

          <!-- Pending Orders -->
          <div v-else class="tab-panel">
            <div class="segmented">
              <button
                class="seg"
                :class="{ active: orderType === 'Buy' }"
                type="button"
                @click="selectOrderType('Buy')"
              >
                Pending Buy
              </button>
              <button
                class="seg"
                :class="{ active: orderType === 'Sell' }"
                type="button"
                @click="selectOrderType('Sell')"
              >
                Pending Sell
              </button>
            </div>

            <div v-if="ordersLoading" class="state-block card">
              <span class="spinner"></span>
            </div>
            <div v-else-if="orders.length" class="stack">
              <OrderCard
                v-for="(o, i) in orders"
                :key="o._id || i"
                :order="o"
                @select="openOrder"
              />
            </div>
            <div v-else class="empty-block card">
              <span class="empty-icon grey"><InvIcon name="receipt" /></span>
              <p class="empty-title">You have no pending trade orders.</p>
            </div>
          </div>
        </section>
      </div>
    </div>


    <!-- Withdrawing releases a real hold, so it gets a considered confirmation rather than a
         browser alert: the alert cannot show which bid, at what level, or what comes back. -->
    <Teleport to="#teleport-modal">
      <SmallModal v-if="pendingWithdraw" @close="cancelWithdraw">
        <template v-slot:content>
          <div class="withdraw-confirm">
            <h3 class="withdraw-confirm__title">Withdraw this bid?</h3>
            <p class="withdraw-confirm__lead">
              Your bid is removed from the book and the funds held for it return to your wallet.
              You can bid again while the offer is still open.
            </p>

            <dl class="withdraw-confirm__facts">
              <div>
                <dt>Offering</dt>
                <dd>{{ pendingWithdraw.token?.name || "—" }}</dd>
              </div>
              <div>
                <dt>Bid</dt>
                <dd>{{ withdrawAmountLabel }}</dd>
              </div>
              <div>
                <dt>Released to your wallet</dt>
                <dd>{{ withdrawHoldLabel }}</dd>
              </div>
            </dl>

            <div class="withdraw-confirm__actions">
              <button
                class="withdraw-confirm__btn withdraw-confirm__btn--quiet"
                type="button"
                :disabled="withdrawing"
                @click="cancelWithdraw"
              >
                Keep my bid
              </button>
              <button
                class="withdraw-confirm__btn withdraw-confirm__btn--danger"
                type="button"
                :disabled="withdrawing"
                @click="confirmWithdraw"
              >
                {{ withdrawing ? "Withdrawing…" : "Withdraw bid" }}
              </button>
            </div>
          </div>
        </template>
      </SmallModal>
    </Teleport>

    <!-- Order detail modal -->
    <Teleport to="#teleport-modal">
      <OrderDetailModal
        v-if="selectedOrder"
        :order="selectedOrder"
        :loading="cancelling"
        @close="closeOrder"
        @cancel="confirmCancel"
      />
    </Teleport>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import SummaryCard from "@/components/invest/SummaryCard.vue";
import SectionHeader from "@/components/invest/SectionHeader.vue";
import GrowthChart from "@/components/invest/GrowthChart.vue";
import AllocationDonut from "@/components/invest/AllocationDonut.vue";
import HoldingRow from "@/components/invest/HoldingRow.vue";
import MaturityCard from "@/components/invest/MaturityCard.vue";
import OrderCard from "@/components/invest/OrderCard.vue";
import CommitmentCard from "@/components/invest/CommitmentCard.vue";
import { resolveCommitmentState } from "@/utils/offering";
import OrderDetailModal from "@/components/invest/OrderDetailModal.vue";
import SmallModal from "@/components/general/SmallModal.vue";
import InvIcon from "@/components/invest/InvIcon.vue";
import { setItem, getItem } from "@/utils/storage.js";
import * as inv from "@/utils/invest.js";

const CURRENCY_KEY = "invest_currency";

// Query values are part of a URL people share, so they stay readable and stable even
// though the internal tab keys differ.
const TABS = { bids: "commitments", orders: "orders", maturities: "maturities" };

export default {
  name: "MyInvestments",

  components: {
    SummaryCard,
    SectionHeader,
    GrowthChart,
    AllocationDonut,
    HoldingRow,
    MaturityCard,
    OrderCard,
    CommitmentCard,
    OrderDetailModal,
    SmallModal,
    InvIcon,
  },

  data() {
    return {
      activeCurrency: inv.DEFAULT_CURRENCY,
      activePeriod: "1M",
      activeTab: "maturities",
      // The bid awaiting confirmation, and whether its request is in flight.
      pendingWithdraw: null,
      withdrawing: false,
      orderType: "Buy",
      orders: [],
      commitments: [],
      ordersLoading: false,
      holdingsLoading: true,
      refreshing: false,
      selectedOrder: null,
      cancelling: false,
    };
  },

  computed: {

    withdrawAmountLabel() {
      const c = this.pendingWithdraw;
      return c ? this.withdrawMoney(c.amount, c.currency) : "—";
    },

    withdrawHoldLabel() {
      const c = this.pendingWithdraw;
      // hold_amount includes the fee charged on top of the bid; it is what actually returns.
      return c ? this.withdrawMoney(c.hold_amount ?? c.amount, c.currency) : "—";
    },
    ...mapState({
      allTokens: (state) => state.TokenModule.unlimitedUserTokens,
      performance: (state) => state.TokenModule.portfolioPerformance,
      performanceLoading: (state) => state.TokenModule.portfolioPerformanceLoading,
    }),

    // Live bids first: they are the ones an investor can still act on, and the settled ones
    // are a record rather than a decision.
    commitmentList() {
      const order = { active: 0, info: 1, success: 2, neutral: 3 };
      return [...this.commitments].sort(
        (a, b) =>
          (order[resolveCommitmentState(a).tone] ?? 9) -
          (order[resolveCommitmentState(b).tone] ?? 9)
      );
    },

    holdings() {
      return inv.currencyHoldings(this.allTokens, this.activeCurrency);
    },
    previewHoldings() {
      return this.holdings.slice(0, 5);
    },
    totalShares() {
      return inv.totalShares(this.holdings);
    },
    totalCashValue() {
      return inv.totalCashValue(this.holdings);
    },
    series() {
      return inv.normalizeSeries(this.performance);
    },
    currentValue() {
      return inv.currentValue(this.series, this.holdings);
    },
    allocation() {
      return inv.allocationByType(this.holdings);
    },
    maturities() {
      return inv.upcomingMaturities(this.holdings);
    },
    periodAgoLabel() {
      return inv.periodConfig(this.activePeriod).ago;
    },
  },

  async created() {
    // Deep-linkable: the deal page's "View my bids" sends ?tab=bids, so arriving
    // from a live bid opens on the bids rather than making you hunt for the tab.
    this.activeTab = TABS[this.$route.query.tab] || this.activeTab;
    this.activeCurrency = getItem(CURRENCY_KEY) || inv.DEFAULT_CURRENCY;
    await this.loadHoldings();
    this.fetchPerformance();
    this.fetchOrders();
    this.fetchCommitments();
  },

  methods: {
    async fetchCommitments() {
      const response = await this.$store.dispatch("TokenModule/getCommitments", { limit: 50 });
      this.commitments = response?.data?.data?.docs || [];
    },

    withdrawMoney(value, currency) {
      return `${currency || ""} ${Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`.trim();
    },

    withdrawBid(commitment) {
      this.pendingWithdraw = commitment;
    },

    cancelWithdraw() {
      if (this.withdrawing) return;
      this.pendingWithdraw = null;
    },

    async confirmWithdraw() {
      const commitment = this.pendingWithdraw;
      if (!commitment) return;
      this.withdrawing = true;
      try {
        const response = await this.$store.dispatch(
          "TokenModule/withdrawCommitment",
          commitment._id
        );
        if (!response) return;

        this.pendingWithdraw = null;
        this.showToast({
          description: "Your bid has been withdrawn and the held funds released.",
          display: true,
          type: "success",
        });
        // The hold is released, so the bid list, the spendable balance and the offer_hold
        // bucket have all moved.
        await this.fetchCommitments();
        this.getWalletBalance();
        this.getCurrencyWallets();
      } finally {
        this.withdrawing = false;
      }
    },

    amendBid(commitment) {
      // Changing a bid means re-entering it against the offering's guidance, which the deal
      // page already knows how to ask for. Sending them there beats a second, thinner copy
      // of the same form living here.
      this.$router.push({
        name: "TokenOverview",
        params: { symbol: commitment.token?.symbol, id: commitment.token?._id },
        query: { amend: commitment._id },
      });
    },

    ...mapActions(["showToast"]),
    ...mapActions("organisationModule", ["getWalletBalance", "getCurrencyWallets"]),
    ...mapActions("TokenModule", [
      "getUnlimitedUserTokens",
      "getPortfolioPerformance",
      "getOrderBooks",
      "cancelOrder",
    ]),

    async loadHoldings() {
      this.holdingsLoading = true;
      await this.getUnlimitedUserTokens({ page: 1, limit: 100000 });
      this.holdingsLoading = false;
    },

    fetchPerformance() {
      const cfg = inv.periodConfig(this.activePeriod);
      this.getPortfolioPerformance({
        range: cfg.range,
        interval: cfg.interval,
        currency: this.activeCurrency,
      });
    },

    async fetchOrders() {
      this.ordersLoading = true;
      // Orders keep their own currency; scoped only by type + pending status.
      const result = await this.getOrderBooks({
        page: 1,
        limit: 20,
        type: this.orderType,
        status: "Active",
      });
      this.orders = (result && result.orders) || [];
      this.ordersLoading = false;
    },

    selectCurrency(c) {
      this.activeCurrency = c;
      setItem(CURRENCY_KEY, c);
      // Re-scope: performance refetches; holdings/maturities recompute reactively.
      this.fetchPerformance();
    },

    selectPeriod(key) {
      if (key === this.activePeriod) return;
      this.activePeriod = key;
      this.fetchPerformance();
    },

    selectOrderType(type) {
      if (type === this.orderType) return;
      this.orderType = type;
      this.fetchOrders();
    },

    async refreshAll() {
      this.refreshing = true;
      await this.loadHoldings();
      this.fetchPerformance();
      await this.fetchOrders();
      this.refreshing = false;
    },

    goToHolding(token) {
      if (inv.isEntityHolding(token)) {
        // ESOP is not routed in this app, so entity holdings are not navigable.
        return;
      }
      this.$router.push({
        name: "TokenOverview",
        params: { symbol: token.symbol, id: token._id || token.id },
      });
    },

    seeAllHoldings() {
      this.$router.push({
        name: "Wallet",
        query: { tab: "investments", currency: this.activeCurrency },
      });
    },

    openOrder(order) {
      this.selectedOrder = order;
    },

    closeOrder() {
      this.selectedOrder = null;
      // Reload orders on close (spec §7b).
      this.fetchOrders();
    },

    async confirmCancel(order) {
      this.cancelling = true;
      const ok = await this.cancelOrder(order._id);
      this.cancelling = false;
      if (ok) {
        this.selectedOrder = null;
        this.fetchOrders();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#invest-page {
  padding-bottom: 40px;
}

/* Header */
.page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.page-title {
  margin: 0;
  font-size: 22px;
  font-weight: 500;
  color: #001240;

  @media (min-width: 1024px) {
    font-size: 28px;
  }
}

.page-subtitle {
  margin: 4px 0 0;
  font-size: 11px;
  color: #4f5877;

  @media (min-width: 1024px) {
    font-size: 13px;
  }
}

.refresh-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #d0d5e0;
  border-radius: 10px;
  background: #ffffff;
  color: #4f5877;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    border-color: #4e84ff;
    color: #4e84ff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.refresh-icon {
  font-size: 16px;
}

/* Layout */
.invest-layout {
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 1360px;
  margin: 0 auto;

  @media (min-width: 1024px) {
    gap: 28px;
  }
}

.section {
  min-width: 0;
}

/* Performance: growth chart takes the room, donut sits beside it */
.perf-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 1.9fr) minmax(300px, 1fr);
    gap: 20px;
    align-items: stretch;
  }
}

.growth,
.allocation {
  min-width: 0;
}

/* Portfolio + Activity side by side on desktop */
.lower-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
    gap: 28px;
    align-items: start;
  }
}

/* Generic card */
.card {
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 8px;
}

.list-card {
  padding: 6px 12px;
}

.stack {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Active badge */
.active-badge {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 12px;
  border-radius: 10px;
  background: rgba(78, 132, 255, 0.1);
  line-height: 1.1;
}

.active-count {
  font-size: 13px;
  font-weight: 500;
  color: #4e84ff;
}

.active-word {
  font-size: 9px;
  color: #4e84ff;
}

/* See all */
.see-all {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin: 6px 0;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: rgba(78, 132, 255, 0.1);
  color: #4e84ff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.see-all-icon {
  font-size: 16px;
}

/* Tabs */
.tabbar {
  display: flex;
  gap: 20px;
  border-bottom: 1px solid #e8ecf4;
  margin-bottom: 16px;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }
}

.tab {
  flex-shrink: 0;
  padding: 10px 0;
  border: none;
  background: transparent;
  color: #4f5877;
  font-size: 12px;
  font-weight: 300;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  white-space: nowrap;

  &.active {
    color: #001240;
    font-weight: 500;
    border-bottom-color: #001240;
  }
}

.tab-panel {
  min-width: 0;
}

/* Segmented control */
.segmented {
  display: flex;
  gap: 4px;
  padding: 4px;
  background: #f5f7fa;
  border-radius: 10px;
  margin-bottom: 14px;
}

.seg {
  flex: 1;
  padding: 8px 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #4f5877;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.15s ease;

  &.active {
    background: #ffffff;
    color: #001240;
    font-weight: 500;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
}

/* States */
.state-block {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.empty-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 40px 16px;
  text-align: center;
}

.empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  font-size: 22px;
  margin-bottom: 4px;

  &.blue {
    background: rgba(78, 132, 255, 0.1);
    color: #4e84ff;
  }
  &.grey {
    background: #f5f7fa;
    color: #8791a7;
  }
}

.empty-title {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: #001240;
}

.empty-sub {
  margin: 0;
  font-size: 11px;
  color: #8791a7;
}

/* Spinners */
.spinner {
  width: 22px;
  height: 22px;
  border: 2px solid #d0d5e0;
  border-top-color: #4e84ff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.spinner-sm {
  width: 14px;
  height: 14px;
  border: 2px solid #d0d5e0;
  border-top-color: #4e84ff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.withdraw-confirm__title {
  font-size: 18px;
  font-weight: 600;
  color: #0F172A;
}

.withdraw-confirm__lead {
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: #475569;
}

.withdraw-confirm__facts {
  margin: 20px 0 0;
  padding: 14px 16px;
  background: #F8FAFC;
  border-radius: 8px;

  div {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 6px 0;
  }

  dt {
    font-size: 13px;
    color: #64748B;
  }

  dd {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #0F172A;
    text-align: right;
  }
}

.withdraw-confirm__actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.withdraw-confirm__btn {
  flex: 1;
  padding: 11px 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #4D84FF;
    outline-offset: 2px;
  }

  &--quiet {
    background: #FFFFFF;
    border-color: #E2E8F0;
    color: #0F172A;

    &:hover:not(:disabled) {
      background: #F8FAFC;
    }
  }

  &--danger {
    background: #EF4444;
    color: #FFFFFF;

    &:hover:not(:disabled) {
      background: #DC2626;
    }
  }
}
</style>
